import { MenuOutlined } from "@ant-design/icons";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Select, Space } from "antd";
import classNames from "classnames";
import AppLoader from "components/AppLoader";
import AuthorisedContainer from "components/AuthorisedContainer";
import { DeleteIcon } from "components/Icons";
import { PermissionSetMap } from "constants/RBACConstants";
import useAPIResponse from "hooks/useAPIResponse";
import _ from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import ExceptionAPI from "restAPI/ExceptionAPI";
import UserAPI from "restAPI/UserAPI";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import { UserResponseDto } from "types/api/userResponseDto";
import { LabelValue } from "types/common";
import { hasPermission } from "utils/authService";
import { filterVocabularyOption } from "utils/formUtils";

export type ApproverProps = {
  value?: Array<number>;
  disableDND?: boolean;
  onChange?: (value: Array<number>) => void;
  scrollY?: number;
  approversRef?: React.MutableRefObject<Array<UserResponseDto>>;
};

export type ApproversType = LabelValue & {
  type: "ROLE" | "USER";
};

interface DraggableApproverItemProps {
  id: number;
  approverData: number;
  index?: number;
  disabled: boolean;
  onRemove: (id: number) => void;
  userDetail?: UserInfoProjectorImpl;
  roleDetail?: ApproversType;
  hasPermissionToUpdate: boolean;
}

const DraggableApproverItem: React.FC<DraggableApproverItemProps> = ({
  id,
  approverData,
  disabled,
  onRemove,
  userDetail,
  roleDetail,
  hasPermissionToUpdate,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  // Apply transform styles for smooth drag animation
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  // Get display name based on approver type
  const displayName = userDetail?.displayName || roleDetail?.label;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="p-y-xs"
      key={id}
    >
      <Space>
        <MenuOutlined
          {...listeners}
          className="p-l-xs"
          style={{
            cursor: disabled ? "not-allowed" : "grab",
          }}
        />
        <span title={displayName as string}>{displayName}</span>
      </Space>
      <AuthorisedContainer permission={PermissionSetMap.MODIFY_APPROVERS_LIST}>
        <DeleteIcon
          onClick={() => {
            if (hasPermissionToUpdate) {
              onRemove(approverData);
            }
          }}
          className={classNames("pull-right", {
            disabled: !hasPermissionToUpdate,
          })}
        />
      </AuthorisedContainer>
    </div>
  );
};

const Approvers = ({
  value = [],
  disableDND = false,
  onChange,
  approversRef,
  scrollY = 200,
}: ApproverProps) => {
  const [loading, setLoading] = useState(false);
  const [newApprovers, setNewApprovers] = useState<number[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);

  const hasPermissionToUpdate = hasPermission(
    PermissionSetMap.MODIFY_APPROVERS_LIST,
  );

  const [allUsers] = useAPIResponse<UserInfoProjectorImpl[]>(
    UserAPI.getAll,
    [],
  );

  const [approvers] = useAPIResponse<ApproversType[]>(() => {
    setLoading(true);
    const promiseObject = ExceptionAPI.getApprovers();
    promiseObject.promise = promiseObject.promise
      .then((approvers) => {
        const userMap = approvers.map((d: UserResponseDto) => ({
          value: d.id,
          label: `${d.displayName}${d.type === "ROLE" ? "(Role)" : ""}`,
          type: d.type,
        }));
        // This is to avoid fetching approvers in parent component
        if (approversRef?.current) {
          approversRef.current = [...userMap];
        }
        return userMap;
      })
      .finally(() => {
        setLoading(false);
      });
    return promiseObject;
  }, []);

  const roles = useMemo(() => {
    return _.filter(approvers, (entry) => entry.type === "ROLE");
  }, [approvers]);

  // Configure sensors for pointer (mouse/touch)
  const sensors = useSensors(useSensor(PointerSensor));

  const updateApprovers = useCallback(
    (currentOrder) => {
      onChange && onChange(currentOrder);
    },
    [onChange],
  );

  // Handle drag end event from dnd-kit
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // When the drag ends, clear the active item's ID to hide the overlay
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = value.findIndex((v) => v === active.id);
      const newIndex = value.findIndex((v) => v === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const currentOrder = [...value];
        const [dragItem] = currentOrder.splice(oldIndex, 1);
        currentOrder.splice(newIndex, 0, dragItem);

        updateApprovers(currentOrder);
      }
    },
    [value, updateApprovers],
  );

  const onAddApprover = useCallback(() => {
    updateApprovers(_.uniq([...value, ...(newApprovers || [])]));
    setNewApprovers([]);
  }, [value, updateApprovers, newApprovers]);

  const onRemoveApprover = useCallback(
    (removedUserId) => {
      updateApprovers(value.filter((userId) => userId !== removedUserId));
    },
    [value, updateApprovers],
  );

  const availableApproversList = useMemo(() => {
    return _.filter(
      approvers,
      (approver) => value.indexOf(+approver.value) === -1,
    );
  }, [approvers, value]);

  return (
    <>
      <div style={{ height: scrollY, overflow: "auto", paddingRight: 8 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragStart={({ active }) => setActiveId(active.id as number)}
        >
          {loading ? (
            <AppLoader />
          ) : (
            <>
              <SortableContext
                items={value}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {value.length === 0 && (
                    <div className="text-center">
                      Approval workflow not defined
                    </div>
                  )}
                  {value.map((entry, index) => {
                    const userDetail = _.find(allUsers, { id: entry });
                    let roleDetail: ApproversType | undefined;
                    if (!userDetail) {
                      roleDetail = _.find(
                        roles,
                        (role) => +role.value === entry,
                      );
                    }
                    return (
                      <DraggableApproverItem
                        key={entry}
                        id={entry}
                        approverData={entry}
                        index={index}
                        disabled={disableDND || !hasPermissionToUpdate}
                        onRemove={onRemoveApprover}
                        userDetail={userDetail}
                        roleDetail={roleDetail}
                        hasPermissionToUpdate={hasPermissionToUpdate}
                      />
                    );
                  })}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeId !== null && (
                  <DraggableApproverItem
                    id={activeId}
                    approverData={activeId}
                    userDetail={_.find(allUsers, { id: activeId })}
                    roleDetail={_.find(
                      roles,
                      (role) => +role.value === activeId,
                    )}
                    hasPermissionToUpdate={hasPermissionToUpdate}
                    disabled={disableDND}
                    onRemove={onRemoveApprover}
                  />
                )}
              </DragOverlay>
            </>
          )}
        </DndContext>
      </div>
      <div className="d-flex">
        <div className="flex-1 m-r-sm">
          <AuthorisedContainer
            permission={PermissionSetMap.MODIFY_APPROVERS_LIST}
          >
            <Select
              placeholder="Add approver"
              className="w-100"
              mode="multiple"
              loading={loading}
              options={availableApproversList}
              filterOption={filterVocabularyOption}
              value={newApprovers}
              onChange={setNewApprovers}
            />
          </AuthorisedContainer>
        </div>
        <AuthorisedContainer
          permission={PermissionSetMap.MODIFY_APPROVERS_LIST}
        >
          <Button onClick={onAddApprover}>Add</Button>
        </AuthorisedContainer>
      </div>
    </>
  );
};

export default Approvers;
